import { Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { TAB_STATUSES } from "../../../../../../constants";
import { useUrlParams } from "../../../../../../hooks";
import RotateLeft from "../../../../../../icons/RotateLeft";
import TrashNoStripes from "../../../../../../icons/TrashNoStripes";
import { visualBuilderDataSelector } from "../../../../../../store/selectors";

const useStyles = makeStyles({
  container: {
    position: "absolute",
    right: "16px",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    top: (props) => (props.tabStatus === TAB_STATUSES.HOMEBASE ? "16px" : "56px"),
    marginRight: (props) => (props.rightDrawer && !props.rightDrawerAutoHide && !isMobile ? 240 : 50),
  },
  volume: {
    padding: 8,
    background: "rgba(0, 0, 0, 0.5)",
    height: "24px",
    boxSizing: "content-box",
    display: "flex",
    "& > div": {
      flex: 1,
      height: "100%",
      width: "4px",
    },
  },
  summary: {
    display: "flex",
    justifyContent: "flex-end",
    background: "black",
    padding: "8px",
    marginBottom: "8px",
    alignItems: "center",
    color: "white",
    gap: "16px",
  },
  pageButton: {
    borderRadius: 24,
    background: "#F50561",
    height: "32px",
    width: "70px",
  },
  divider: {
    width: "1px",
    background: "white",
    margin: "-8px 0px",
    height: "48px",
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    cursor: "pointer",
  },
  iconDisabled: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    width: "24px",
    height: "24px",
    opacity: 0.5,
  },
});

const AMOUNT_AUDIO_BARS = 35;

// interface AudioRecordingControlsProps {
//   onDelete: proptypes.func,
//   onRestart: proptypes.func,
//   onStop: proptypes.func,
//   open: proptypes.object,
//   state: proptypes.oneOf(["idle", "preparing", "recording"]),
// };

export default function AudioRecordingControls(props) {
  const { stream, state, onStop, onRestart, onDelete } = props;

  const [msElapsed, setMsElapsed] = useState(0);

  const params = useUrlParams();
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));

  const { rightDrawerAutoHide = false, rightDrawer = true } = visualBuilderData;

  const classes = useStyles({ tabStatus: params.tabStatus, rightDrawerAutoHide, rightDrawer });

  const refVolume = useRef();
  const startTime = useRef(0);
  const analyzer = useRef();
  const analyzerArray = useRef();

  const createAnalyzer = () => {
    const context = new AudioContext();
    analyzer.current = context.createAnalyser();
    context.createMediaStreamSource(stream).connect(analyzer.current);
    analyzerArray.current = new Uint8Array(analyzer.current.fftSize);
  };

  const analyzeAudioLevels = () => {
    if (analyzer.current && refVolume.current) {
      analyzer.current.getByteTimeDomainData(analyzerArray.current);

      // The byte values range between 0-255, that maps to -1 to +1, so 128 is zero
      const volume = analyzerArray.current.reduce((max, current) => Math.max(current, max, 128), 0);
      const percentage = volume / 128 - 1;

      const children = refVolume.current.children;
      for (let i = 0; i < children.length; i++) {
        if (i % 2 !== 0) {
          children[i].style.background = "#000000";
        } else if (percentage * AMOUNT_AUDIO_BARS > i) {
          children[i].style.background = "#009400";
        } else {
          children[i].style.background = "#ffffff";
        }
      }

      requestAnimationFrame(analyzeAudioLevels);
    }
  };

  useEffect(() => {
    if (stream) {
      createAnalyzer();
      requestAnimationFrame(analyzeAudioLevels);
      return () => (analyzer.current = null);
    }
  }, [stream]);

  useEffect(() => {
    if (state === "recording") {
      startTime.current = Date.now();
      const interval = setInterval(() => setMsElapsed(Date.now() - startTime.current), 1000);
      return () => clearInterval(interval);
    } else {
      setMsElapsed(0);
    }
  }, [state]);

  useEffect(() => {
    if (msElapsed >= 300000) {
      // 5 minutes
      onStop();
      toast.info("Recording time limit reached", { containerId: "dark" });
    }
  }, [msElapsed]);

  return (
    <div className={classes.container} data-testid="controls-container">
      <div className={classes.summary}>
        <div
          aria-hidden="true"
          className={state === "recording" ? classes.icon : classes.iconDisabled}
          onClick={state === "recording" ? () => onDelete() : null}
          data-testid="delete"
        >
          <TrashNoStripes />
        </div>
        <div
          aria-hidden="true"
          className={state === "recording" ? classes.icon : classes.iconDisabled}
          onClick={state === "recording" ? () => onRestart() : null}
          data-testid="restart"
        >
          <RotateLeft />
        </div>

        <div className={classes.divider} />

        <span>{formatMilliseconds(state === "recording" ? msElapsed : 0)}</span>

        <Button className={classes.pageButton} color="primary" variant="contained" onClick={() => onStop()}>
          {state === "recording" ? "Done" : "Cancel"}
        </Button>
      </div>

      <div className={classes.volume} ref={refVolume} data-testid="volume-container">
        {new Array(AMOUNT_AUDIO_BARS).fill(0).map((_, i) => (
          <div key={i} style={{ background: i % 2 === 0 ? "white" : "black" }} data-testid={`audio-bar-${i}`} />
        ))}
      </div>
    </div>
  );
}

function formatMilliseconds(millis) {
  const minutes = Math.floor(millis / 60000);
  const seconds = millis ? ((millis % 60000) / 1000).toFixed(0) : 0;
  return minutes + ":" + String(seconds).padStart(2, "0");
}
